import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Switch,
  FormControlLabel,
  Divider,
  Alert,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  Chip,
} from '@mui/material';
import { Save, Add, Edit, Delete, Refresh } from '@mui/icons-material';
import { fetchGeneralConfig, updateGeneralConfig } from '../store/generalConfigSlice';
import { 
  fetchTradingConfigs, 
  createTradingConfig, 
  updateTradingConfig, 
  deleteTradingConfig 
} from '../store/tradingConfigSlice';
import { fetchMarkets } from '../store/marketSlice';
import { ExchangeIcon } from '../components/IconDisplay';
import { useConfirm } from '../components/ConfirmDialog';

function Settings() {
  const dispatch = useDispatch();
  const { confirm, dialog: confirmDialog } = useConfirm();
  const { config: generalConfig, loading: generalLoading } = useSelector((state) => state.generalConfig);
  const { configs: tradingConfigs = [], loading: tradingLoading } = useSelector((state) => state.tradingConfigs);
  const { markets = [] } = useSelector((state) => state.markets);
  
  const [saved, setSaved] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  
  // General config form state
  const [generalForm, setGeneralForm] = useState({
    enable_notification: true,
    telegram_notification_number: 1,
    telegram_bot_token: '',
    telegram_chat_id: '',
  });
  
  // Trading config form state
  const [tradingForm, setTradingForm] = useState({
    target_market: '',
    target_market_margin_mode: 'isolated',
    target_market_leverage: 1,
    target_market_stop_loss_p: '',
    target_market_margin_call_action: 'warning',
    origin_market: '',
    origin_market_margin_mode: 'isolated',
    origin_market_leverage: 1,
    origin_market_stop_loss_p: '',
    origin_market_margin_call_action: 'warning',
    safe_reverse_trading: true,
  });

  useEffect(() => {
    dispatch(fetchGeneralConfig());
    dispatch(fetchTradingConfigs());
    dispatch(fetchMarkets());
  }, [dispatch]);

  useEffect(() => {
    if (generalConfig) {
      setGeneralForm({
        enable_notification: generalConfig.enable_notification,
        telegram_notification_number: generalConfig.telegram_notification_number,
        telegram_bot_token: generalConfig.telegram_bot_token || '',
        telegram_chat_id: generalConfig.telegram_chat_id || '',
      });
    }
  }, [generalConfig]);

  const handleGeneralChange = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setGeneralForm({
      ...generalForm,
      [field]: value,
    });
  };

  const handleSaveGeneralConfig = async () => {
    try {
      await dispatch(updateGeneralConfig(generalForm)).unwrap();
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (error) {
      console.error('Failed to save general config:', error);
    }
  };

  const handleOpenDialog = (config = null) => {
    if (config) {
      setEditingConfig(config);
      const isTargetSpot = isSpotMarket(config.target_market);
      const isOriginSpot = isSpotMarket(config.origin_market);
      
      setTradingForm({
        target_market: config.target_market,
        target_market_margin_mode: isTargetSpot ? null : (config.target_market_margin_mode || 'isolated'),
        target_market_leverage: isTargetSpot ? null : (config.target_market_leverage || 1),
        target_market_stop_loss_p: isTargetSpot ? null : (config.target_market_stop_loss_p || ''),
        target_market_margin_call_action: isTargetSpot ? null : (config.target_market_margin_call_action || 'warning'),
        origin_market: config.origin_market,
        origin_market_margin_mode: isOriginSpot ? null : (config.origin_market_margin_mode || 'isolated'),
        origin_market_leverage: isOriginSpot ? null : (config.origin_market_leverage || 1),
        origin_market_stop_loss_p: isOriginSpot ? null : (config.origin_market_stop_loss_p || ''),
        origin_market_margin_call_action: isOriginSpot ? null : (config.origin_market_margin_call_action || 'warning'),
        safe_reverse_trading: config.safe_reverse_trading,
      });
    } else {
      setEditingConfig(null);
      setTradingForm({
        target_market: '',
        target_market_margin_mode: 'isolated',
        target_market_leverage: 1,
        target_market_stop_loss_p: '',
        target_market_margin_call_action: 'warning',
        origin_market: '',
        origin_market_margin_mode: 'isolated',
        origin_market_leverage: 1,
        origin_market_stop_loss_p: '',
        origin_market_margin_call_action: 'warning',
        safe_reverse_trading: true,
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingConfig(null);
  };

  const handleTradingFormChange = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    
    // Handle market selection changes
    if (field === 'target_market' || field === 'origin_market') {
      const isSpot = isSpotMarket(value);
      const prefix = field === 'target_market' ? 'target_market' : 'origin_market';
      
      setTradingForm({
        ...tradingForm,
        [field]: value,
        // Reset margin-related fields for spot markets
        [`${prefix}_margin_mode`]: isSpot ? null : tradingForm[`${prefix}_margin_mode`] || 'isolated',
        [`${prefix}_leverage`]: isSpot ? null : tradingForm[`${prefix}_leverage`] || 1,
        [`${prefix}_stop_loss_p`]: isSpot ? null : tradingForm[`${prefix}_stop_loss_p`] || '',
        [`${prefix}_margin_call_action`]: isSpot ? null : tradingForm[`${prefix}_margin_call_action`] || 'warning',
      });
    } else {
      setTradingForm({
        ...tradingForm,
        [field]: value,
      });
    }
  };

  const handleSaveTradingConfig = async () => {
    try {
      if (editingConfig) {
        await dispatch(updateTradingConfig({ 
          uuid: editingConfig.uuid, 
          data: tradingForm 
        })).unwrap();
      } else {
        await dispatch(createTradingConfig(tradingForm)).unwrap();
      }
      handleCloseDialog();
      dispatch(fetchTradingConfigs());
    } catch (error) {
      console.error('Failed to save trading config:', error);
    }
  };

  const handleDeleteTradingConfig = async (uuid) => {
    const ok = await confirm({
      title: 'Delete trading configuration?',
      message: 'This configuration will be removed permanently.',
      confirmLabel: 'Delete',
      severity: 'error',
    });
    if (!ok) return;
    try {
      await dispatch(deleteTradingConfig(uuid)).unwrap();
      dispatch(fetchTradingConfigs());
    } catch (error) {
      console.error('Failed to delete trading config:', error);
    }
  };

  const handleRefresh = () => {
    dispatch(fetchGeneralConfig());
    dispatch(fetchTradingConfigs());
  };

  // Group markets by exchange
  const marketsByExchange = markets.reduce((acc, market) => {
    const exchangeName = market.exchange_name || 'Unknown';
    if (!acc[exchangeName]) {
      acc[exchangeName] = [];
    }
    acc[exchangeName].push(market);
    return acc;
  }, {});

  // Helper function to check if a market is spot
  const isSpotMarket = (marketId) => {
    const market = markets.find(m => m.id === marketId);
    return market && market.name === 'SPOT';
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5">Settings</Typography>
        <IconButton onClick={handleRefresh} disabled={generalLoading || tradingLoading} aria-label="Refresh settings">
          <Refresh />
        </IconButton>
      </Box>

      {saved && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Settings saved successfully!
        </Alert>
      )}

      {/* General Configuration */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          General Configuration
        </Typography>
        <Divider sx={{ mb: 2 }} />
        
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={generalForm.enable_notification}
                  onChange={handleGeneralChange('enable_notification')}
                />
              }
              label="Enable Notifications"
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Telegram Notification Number"
              value={generalForm.telegram_notification_number}
              onChange={handleGeneralChange('telegram_notification_number')}
              type="number"
              disabled={!generalForm.enable_notification}
              helperText="Number of notifications to send"
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Telegram Bot Token"
              value={generalForm.telegram_bot_token}
              onChange={handleGeneralChange('telegram_bot_token')}
              type="password"
              disabled={!generalForm.enable_notification}
              helperText="Your Telegram bot token for notifications"
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Telegram Chat ID"
              value={generalForm.telegram_chat_id}
              onChange={handleGeneralChange('telegram_chat_id')}
              disabled={!generalForm.enable_notification}
              helperText="Your Telegram chat ID to receive notifications"
            />
          </Grid>
          
          <Grid item xs={12}>
            <Box display="flex" justifyContent="flex-end">
              <Button
                variant="contained"
                startIcon={<Save />}
                onClick={handleSaveGeneralConfig}
                disabled={generalLoading}
              >
                Save General Configuration
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Trading Configuration */}
      <Paper sx={{ p: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">
            Trading Configuration
          </Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpenDialog()}
          >
            Add Trading Config
          </Button>
        </Box>
        <Divider sx={{ mb: 2 }} />
        
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Target Market</TableCell>
                <TableCell>Origin Market</TableCell>
                <TableCell>Target Leverage</TableCell>
                <TableCell>Origin Leverage</TableCell>
                <TableCell>Safe Reverse</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tradingConfigs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    No trading configurations found
                  </TableCell>
                </TableRow>
              ) : (
                tradingConfigs.map((config) => (
                  <TableRow key={config.uuid}>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <ExchangeIcon exchangeName={config.target_exchange_name} size={20} />
                        {config.target_exchange_name} - {config.target_market_name}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <ExchangeIcon exchangeName={config.origin_exchange_name} size={20} />
                        {config.origin_exchange_name} - {config.origin_market_name}
                      </Box>
                    </TableCell>
                    <TableCell>{config.target_market_leverage || '-'}x</TableCell>
                    <TableCell>{config.origin_market_leverage || '-'}x</TableCell>
                    <TableCell>
                      <Chip 
                        label={config.safe_reverse_trading ? 'Yes' : 'No'} 
                        color={config.safe_reverse_trading ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDialog(config)}
                          aria-label={`Edit ${config.name || 'trading'} configuration`}
                        >
                          <Edit />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteTradingConfig(config.uuid)}
                          aria-label={`Delete ${config.name || 'trading'} configuration`}
                        >
                          <Delete />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Trading Config Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingConfig ? 'Edit Trading Configuration' : 'Create Trading Configuration'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>
                Target Market Settings
              </Typography>
              <Divider />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>Target Market</InputLabel>
                <Select
                  value={tradingForm.target_market}
                  onChange={handleTradingFormChange('target_market')}
                  label="Target Market"
                >
                  {Object.entries(marketsByExchange).map(([exchange, exchangeMarkets]) => [
                    <MenuItem key={`header-${exchange}`} disabled>
                      <Box display="flex" alignItems="center" gap={1}>
                        <ExchangeIcon exchangeName={exchange} size={20} />
                        <strong>{exchange}</strong>
                      </Box>
                    </MenuItem>,
                    ...exchangeMarkets.map((market) => (
                      <MenuItem key={market.id} value={market.id}>
                        <Box display="flex" alignItems="center" gap={1} sx={{ pl: 2 }}>
                          <ExchangeIcon exchangeName={exchange} size={16} />
                          {market.name}
                        </Box>
                      </MenuItem>
                    ))
                  ])}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                label="Margin Mode"
                value={tradingForm.target_market_margin_mode || ''}
                onChange={handleTradingFormChange('target_market_margin_mode')}
                disabled={isSpotMarket(tradingForm.target_market)}
                helperText={isSpotMarket(tradingForm.target_market) ? "Not applicable for spot markets" : ""}
              >
                <MenuItem value="isolated">Isolated</MenuItem>
                <MenuItem value="cross">Cross</MenuItem>
              </TextField>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Leverage"
                value={tradingForm.target_market_leverage || ''}
                onChange={handleTradingFormChange('target_market_leverage')}
                type="number"
                inputProps={{ min: 1, max: 125 }}
                disabled={isSpotMarket(tradingForm.target_market)}
                helperText={isSpotMarket(tradingForm.target_market) ? "Not applicable for spot" : ""}
              />
            </Grid>
            
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Stop Loss %"
                value={tradingForm.target_market_stop_loss_p || ''}
                onChange={handleTradingFormChange('target_market_stop_loss_p')}
                type="number"
                disabled={isSpotMarket(tradingForm.target_market)}
                helperText={isSpotMarket(tradingForm.target_market) ? "Not applicable for spot" : "Leave empty to disable"}
              />
            </Grid>
            
            <Grid item xs={12} md={4}>
              <TextField
                select
                fullWidth
                label="Margin Call Action"
                value={tradingForm.target_market_margin_call_action || ''}
                onChange={handleTradingFormChange('target_market_margin_call_action')}
                disabled={isSpotMarket(tradingForm.target_market)}
                helperText={isSpotMarket(tradingForm.target_market) ? "Not applicable for spot" : ""}
              >
                <MenuItem value="warning">Warning</MenuItem>
                <MenuItem value="exit">Exit</MenuItem>
                <MenuItem value="ignore">Ignore</MenuItem>
              </TextField>
            </Grid>
            
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
                Origin Market Settings
              </Typography>
              <Divider />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>Origin Market</InputLabel>
                <Select
                  value={tradingForm.origin_market}
                  onChange={handleTradingFormChange('origin_market')}
                  label="Origin Market"
                >
                  {Object.entries(marketsByExchange).map(([exchange, exchangeMarkets]) => [
                    <MenuItem key={`header-${exchange}`} disabled>
                      <Box display="flex" alignItems="center" gap={1}>
                        <ExchangeIcon exchangeName={exchange} size={20} />
                        <strong>{exchange}</strong>
                      </Box>
                    </MenuItem>,
                    ...exchangeMarkets.map((market) => (
                      <MenuItem key={market.id} value={market.id}>
                        <Box display="flex" alignItems="center" gap={1} sx={{ pl: 2 }}>
                          <ExchangeIcon exchangeName={exchange} size={16} />
                          {market.name}
                        </Box>
                      </MenuItem>
                    ))
                  ])}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                label="Margin Mode"
                value={tradingForm.origin_market_margin_mode || ''}
                onChange={handleTradingFormChange('origin_market_margin_mode')}
                disabled={isSpotMarket(tradingForm.origin_market)}
                helperText={isSpotMarket(tradingForm.origin_market) ? "Not applicable for spot markets" : ""}
              >
                <MenuItem value="isolated">Isolated</MenuItem>
                <MenuItem value="cross">Cross</MenuItem>
              </TextField>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Leverage"
                value={tradingForm.origin_market_leverage || ''}
                onChange={handleTradingFormChange('origin_market_leverage')}
                type="number"
                inputProps={{ min: 1, max: 125 }}
                disabled={isSpotMarket(tradingForm.origin_market)}
                helperText={isSpotMarket(tradingForm.origin_market) ? "Not applicable for spot" : ""}
              />
            </Grid>
            
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Stop Loss %"
                value={tradingForm.origin_market_stop_loss_p || ''}
                onChange={handleTradingFormChange('origin_market_stop_loss_p')}
                type="number"
                disabled={isSpotMarket(tradingForm.origin_market)}
                helperText={isSpotMarket(tradingForm.origin_market) ? "Not applicable for spot" : "Leave empty to disable"}
              />
            </Grid>
            
            <Grid item xs={12} md={4}>
              <TextField
                select
                fullWidth
                label="Margin Call Action"
                value={tradingForm.origin_market_margin_call_action || ''}
                onChange={handleTradingFormChange('origin_market_margin_call_action')}
                disabled={isSpotMarket(tradingForm.origin_market)}
                helperText={isSpotMarket(tradingForm.origin_market) ? "Not applicable for spot" : ""}
              >
                <MenuItem value="warning">Warning</MenuItem>
                <MenuItem value="exit">Exit</MenuItem>
                <MenuItem value="ignore">Ignore</MenuItem>
              </TextField>
            </Grid>
            
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={tradingForm.safe_reverse_trading}
                    onChange={handleTradingFormChange('safe_reverse_trading')}
                  />
                }
                label="Safe Reverse Trading"
                sx={{ mt: 2 }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSaveTradingConfig} variant="contained">
            {editingConfig ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {confirmDialog}
    </Box>
  );
}

export default Settings;